import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import Layout from '../Layout'
import DataTable from '../../../Components/DataTable'
import Button from '../../../Components/Button'
import FilterPills from '../../../Components/FilterPills'
import ExportOrdersModal from './ExportOrdersModal'
import StarRating from '../../../Components/StarRating'
import {
  formatPrice,
  formatDate,
  getStatusClasses,
  capitalize,
} from '../../../utils/formatters'

export default function Index({
  account,
  accounts,
  navigation,
  orders,
  statuses,
  params = {},
  daily_summary,
  pagination,
}) {
  const [showExportModal, setShowExportModal] = useState(false)

  const handleStatusFilterChange = (status) => {
    router.visit('/manage/orders', {
      data: { ...params, status: status || undefined, page: undefined },
      preserveState: true,
      preserveScroll: true,
    })
  }

  const handleReviewedToggle = () => {
    router.visit('/manage/orders', {
      data: {
        ...params,
        reviewed: params.reviewed ? undefined : '1',
        page: undefined,
      },
      preserveState: true,
      preserveScroll: true,
    })
  }

  const columns = [
    {
      key: 'order_number',
      label: 'Order',
      sortable: true,
      render: (value, item) => (
        <div>
          {item.order_code && (
            <div className="font-semibold text-gray-900">{item.order_code}</div>
          )}
          <div
            className={`${
              item.order_code
                ? 'text-xs font-mono text-gray-500'
                : 'font-medium text-gray-900'
            }`}
          >
            #{value}
          </div>
          {item.pickup_time && (
            <div className="text-sm text-gray-500">
              Pickup: {item.pickup_time}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'email',
      label: 'Customer',
      render: (value, item) => (
        <div>
          <div className="text-gray-900">{value}</div>
          {item.phone && (
            <div className="text-sm text-gray-500">{item.phone}</div>
          )}
        </div>
      ),
    },
    {
      key: 'item_count',
      label: 'Items',
      className: 'text-gray-500',
      render: (value) => `${value} items`,
    },
    {
      key: 'total_pence',
      label: 'Total',
      sortable: true,
      className: 'font-medium text-gray-900',
      render: (value) => formatPrice(value),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <span
          className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusClasses(
            value
          )}`}
        >
          {capitalize(value.replace('_', ' '))}
        </span>
      ),
    },
    {
      key: 'rating',
      label: 'Rating',
      render: (value) =>
        value ? (
          <StarRating rating={value} />
        ) : (
          <span className="text-gray-300">&mdash;</span>
        ),
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      className: 'text-gray-500',
      render: (value) => formatDate(value),
    },
  ]

  return (
    <Layout
      account={account}
      accounts={accounts}
      navigation={navigation}
      title="Orders"
    >
      <div className="mb-6 flex justify-end">
        <Button variant="secondary" onClick={() => setShowExportModal(true)}>
          Export
        </Button>
      </div>

      {/* Today's Summary */}
      {daily_summary && (
        <div className="mb-6 bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Today's Summary
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-gray-900">
                  {daily_summary.order_count}
                </div>
                <div className="text-sm text-gray-500">Orders</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <div className="text-3xl font-bold text-gray-900">
                  {formatPrice(daily_summary.revenue_pence)}
                </div>
                <div className="text-sm text-gray-500">Revenue</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center sm:col-span-2 lg:col-span-1">
                <div className="text-3xl font-bold text-gray-900">
                  {daily_summary.item_breakdown.reduce(
                    (sum, item) => sum + item.count,
                    0
                  )}
                </div>
                <div className="text-sm text-gray-500">Items Sold</div>
              </div>
            </div>

            {daily_summary.item_breakdown.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  Items Breakdown
                </h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Item
                        </th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                          Category
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          Qty
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {daily_summary.item_breakdown.map((item, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {item.name}
                          </td>
                          <td className="px-4 py-2 text-center">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                                item.category === 'food'
                                  ? 'bg-orange-100 text-orange-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}
                            >
                              {item.category}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm text-right font-medium text-gray-900">
                            {item.count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <FilterPills
          items={statuses}
          value={params.status}
          onChange={handleStatusFilterChange}
          allLabel="All Statuses"
        />
        <span
          className="hidden sm:block h-5 w-px bg-gray-300"
          aria-hidden="true"
        />
        <button
          onClick={handleReviewedToggle}
          className={`px-3 py-1.5 text-sm font-medium rounded-full transition ${
            params.reviewed
              ? 'bg-sky-100 text-sky-800 ring-1 ring-sky-600'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          ★ Reviewed
        </button>
      </div>

      <DataTable
        data={orders}
        columns={columns}
        rowHref={(item) => `/manage/orders/${item.id}`}
        pagination={pagination}
        actions={(item) => (
          <Button link href={`/manage/orders/${item.id}`}>
            View
          </Button>
        )}
        searchable={true}
        searchPlaceholder="Search by order #, email, or phone..."
        emptyState={{
          message: 'No orders found',
        }}
        baseUrl="/manage/orders"
        params={params}
      />
      {showExportModal && (
        <ExportOrdersModal onClose={() => setShowExportModal(false)} />
      )}
    </Layout>
  )
}
